import { findIndex, isEmpty, isNil } from 'lodash-es';

import { getQuery } from 'client/utils/location';
import {
  APPRAISER_STEPS,
  PARTNER_OFFER_APPRAISER_STEPS,
  CONDITIONS,
  TRADE_IN_VALUE,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { revalidateErrors } from './trade-in-appraiser-validators';

const STEPS_CONFIG = {
  DRAWER: APPRAISER_STEPS,
  EXTENDED: PARTNER_OFFER_APPRAISER_STEPS,
};

/**
 * Gets default state data.
 * @param {Object} params
 * @returns {Object}
 */
export function getDefaultState({
  zipCode,
  isZipCodeValid,
  selectedStyle,
  errorMap = new Map(),
  location,
  isExtendedReport,
  hasVinMismatchError = false,
  isVinFromDrawer = false,
  isEscapeHatch = true,
  predefinedVin = {},
  modsData = {},
  isOfferConditionsStepLoaded = false,
  isLicensePlateAndVinStepLoaded = false,
  isInstantOfferStepLoaded = false,
}) {
  const { zip: queryZip, mileage: queryMileage, condition: queryCondition = '', styleIds } = getQuery(location);
  const modsDataStyleId = modsData.styleId ? modsData.styleId.toString() : '';

  const steps = isExtendedReport ? STEPS_CONFIG.EXTENDED : STEPS_CONFIG.DRAWER;

  return {
    steps,
    zipCode: queryZip || zipCode,
    vehicle: { style: selectedStyle },
    isZipCodeValid,
    mileage: queryMileage || '',
    condition: CONDITIONS[queryCondition.toUpperCase()] || CONDITIONS.AVERAGE,
    exteriorColor: {},
    options: [],
    styleIds: styleIds || modsDataStyleId,
    isMileageValid: true,
    allConditionTotalPrices: {},
    appraisalEmailData: {},
    partnerOfferEmailData: {
      redemptionCertificateUri: '',
      appointmentUri: '',
    },
    emailReportStatus: '',
    payoffAmount: 0,
    errorMap,
    isPartnerOfferEligibleVin: null,
    isVinValid: true,
    areQuestionnaireResponsesValid: true,
    partnerEquipment: [],
    partnerQuestionnaireVerboseResponse: {},
    partnerQuestionnaireAnswers: {},
    vin: predefinedVin.vin || '',
    hasVinMismatchError,
    isVinFromDrawer,
    appraisalReason: '',
    selectedReportType: TRADE_IN_VALUE,
    showAddEquipment: true,
    isEscapeHatch,
    isLoadingColorsAndOptions: false,
    isAllowedToPredefineVin: true,
    chromeStyleIds: null,
    transmissionType: null,
    engine: {},
    isTransmissionTypeTranslationOverride: false,
    isEngineSizeTranslationOverride: false,
    decodedVinEligibility: null,
    prefilledExteriorColorId: null,
    prefilledInteriorColorId: null,
    prefilledOptions: [],
    isOfferConditionsStepLoaded,
    isLicensePlateAndVinStepLoaded,
    isInstantOfferStepLoaded,
    hasExteriorColorsOptions: false,
    isEmailDataReady: false,
    emailFromEmailStep: null,
    onColorsAndOptionsLoaded: null,
    isStyleFromStyleMismatchModal: false,
  };
}

/**
 * Validates a step and updates its isDone state, optionally resetting subsequent steps.
 * @param {Object} newState
 * @param {string} stepId
 * @param {Function} isValidFn
 * @param {Object} props
 * @param {Object} [options]
 * @param {boolean} [options.resetSubsequent=true]
 * @returns {Object}
 */
export function validateAndUpdateStep(newState, stepId, isValidFn, props, { resetSubsequent = true } = {}) {
  const isValid = isValidFn(newState, props);
  const steps = [...newState.steps];
  const index = findIndex(steps, { id: stepId });
  if (index >= 0) {
    steps[index] = { ...steps[index], isDone: isValid };
    if (resetSubsequent) {
      for (let i = index + 1; i < steps.length; i += 1) {
        steps[i] = { ...steps[i], isDone: false };
      }
    }
  }
  return revalidateErrors(stepId, { ...newState, steps }, props.isExtendedReport);
}

/**
 * Processes step updates when changing steps.
 * @param {Array} steps
 * @param {string} activeStepId
 * @param {string} stepIdToBeActive
 * @param {boolean} userSelectedPreviousStep
 * @param {Object} params
 * @returns {Array}
 */
export function processStepsUpdate(
  steps,
  activeStepId,
  stepIdToBeActive,
  userSelectedPreviousStep,
  { hasVinMismatchError, isVinFromDrawer, queryVin }
) {
  const isSkipVinStep = (queryVin || isVinFromDrawer) && !hasVinMismatchError;
  const skipObjPerStep = steps.reduce((acc, step) => {
    const current = step.skipToStepObj || acc[acc.length - 1];
    acc.push(current);
    return acc;
  }, []);

  return steps.map((step, index) => {
    const skipToStepObj = skipObjPerStep[index];
    const processedStep = {
      ...step,
      isDone: step.isDone || (!userSelectedPreviousStep && step.id === activeStepId),
      isActive: step.id === stepIdToBeActive,
    };

    if (!isSkipVinStep || isEmpty(skipToStepObj) || userSelectedPreviousStep) {
      return processedStep;
    }
    const stepIsActiveStep = step.id === activeStepId;
    const { shouldSkipHappen, skipToStepId, markCompleteId, hasBeenSkipped } = skipToStepObj;
    const stepWasSkipped =
      shouldSkipHappen({ queryVin, isVinFromDrawer, hasBeenSkipped }) && stepIdToBeActive === markCompleteId;
    const stepIsSkipToStep = stepWasSkipped && step.id === skipToStepId;

    processedStep.isDone = step.isDone || stepIsActiveStep || (stepWasSkipped && step.id === markCompleteId);
    processedStep.isActive = (step.id === stepIdToBeActive && !stepWasSkipped) || stepIsSkipToStep;

    if (!isNil(processedStep.skipToStepObj)) {
      processedStep.skipToStepObj = {
        ...skipToStepObj,
        hasBeenSkipped: stepIsActiveStep,
      };
    }

    return processedStep;
  });
}
